import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Calendar } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export const HomepageNavbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    // Only handle smooth scroll if we're on the homepage
    if (location.pathname === "/" && href.startsWith("#")) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        setIsMobileMenuOpen(false);
      }
    } else {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100"
          : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <div className="w-10 h-10 bg-[#17265a] flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-[#17265a]">Aiserwave</span>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href="#modules"
              onClick={(e) => handleAnchorClick(e, "#modules")}
              className="text-gray-700 hover:text-[#17265a] transition-colors font-medium"
            >
              Modules
            </a>
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              className="text-gray-700 hover:text-[#17265a] transition-colors font-medium"
            >
              Pricing
            </button>
            <button
              type="button"
              onClick={() => navigate("/about")}
              className="text-gray-700 hover:text-[#17265a] transition-colors font-medium"
            >
              About
            </button>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="bg-[#17265a] text-white px-6 py-2 rounded-lg hover:bg-[#0f1a3f] transition-colors font-medium"
            >
              Login
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-[#17265a] hover:bg-gray-50 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden border-t border-gray-100 bg-white"
        >
          <div className="px-4 py-4 space-y-3">
            <a
              href="#modules"
              onClick={(e) => handleAnchorClick(e, "#modules")}
              className="block text-gray-700 hover:text-[#17265a] transition-colors font-medium py-2"
            >
              Modules
            </a>
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                navigate("/pricing");
              }}
              className="w-full text-left block text-gray-700 hover:text-[#17265a] transition-colors font-medium py-2"
            >
              Pricing
            </button>
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                navigate("/about");
              }}
              className="w-full text-left block text-gray-700 hover:text-[#17265a] transition-colors font-medium py-2"
            >
              About
            </button>
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                navigate("/login");
              }}
              className="w-full bg-[#17265a] text-white px-6 py-2 rounded-lg hover:bg-[#0f1a3f] transition-colors font-medium text-center mt-2"
            >
              Login
            </button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};
